import { motion } from "framer-motion";
import { MEDIA, CONTACT } from "@/data/site";
import { MaskedLines, SectionTag, ClipReveal } from "@/components/Kinetic";
import { EASE, fadeUp, viewport } from "@/lib/motion";

const STRENGTHS = [
  "Editing",
  "Pacing",
  "Storytelling",
  "Motion",
  "Visual systems",
  "AI-assisted production",
  "Fast creative iteration",
];

export const About = () => (
  <section
    id="about"
    data-testid="about"
    className="relative border-t border-[#1f1f1f] py-24 md:py-36"
  >
    <div className="mx-auto max-w-[1700px] px-5 md:px-10">
      <SectionTag num="07 / ABOUT">HOW I THINK</SectionTag>

      <div className="grid gap-14 lg:grid-cols-12 lg:gap-20">
        <div className="lg:col-span-7">
          <MaskedLines
            lines={["I DON'T JUST CUT", "FOOTAGE. I LOOK FOR", "THE MOMENT THAT", "MAKES SOMEONE STAY."]}
            className="font-display text-2xl font-black uppercase leading-[0.95] tracking-[-0.035em] text-[#e5e5e5] md:text-[3vw]"
            testId="about-headline"
          />

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            custom={4}
            className="mt-12 max-w-xl space-y-5 text-sm leading-relaxed text-[#a3a3a3] md:text-base"
          >
            <p>
              I'm Harsh — HSK. I edit video for creators and brands worldwide
              who need their content to actually land on a feed rather
              than in a folder.
            </p>
            <p>
              Most of the job happens before the effects: deciding what the
              viewer needs to feel, in what order, and how long they'll give
              you. Everything after that is execution.
            </p>
            <p>
              I use AI where it removes a wait — ideation, exploration, voice
              prototyping, generative visuals. The creative calls stay mine.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-2 gap-x-8 border-t border-[#1f1f1f] pt-8 md:grid-cols-3">
            {STRENGTHS.map((s, i) => (
              <motion.span
                key={s}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewport}
                transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                className="border-b border-[#1f1f1f] py-3 label text-[#c4c4c4]"
              >
                {s}
              </motion.span>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <ClipReveal className="relative aspect-[3/4] overflow-hidden">
            <img
              src={MEDIA.studioDesk}
              alt="Editing setup"
              loading="lazy"
              className="h-full w-full object-cover grayscale"
            />
            <div className="absolute inset-0 bg-[#0a0a0a]/40" />
            <div className="scanlines absolute inset-0 opacity-40" />
          </ClipReveal>

          <dl className="mt-8 border-t border-[#1f1f1f]">
            {[
              ["NAME", "HARSH KOTWANI"],
              ["ALIAS", "HSK®"],
              ["ROLE", "VIDEO EDITOR"],
              ["AVAILABILITY", CONTACT.availability],
              ["WORKS WITH", "CREATORS + BRANDS"],
            ].map(([k, v]) => (
              <div
                key={k}
                className="flex items-baseline justify-between border-b border-[#1f1f1f] py-4"
              >
                <dt className="label text-[#7a7a7a]">{k}</dt>
                <dd className="label text-[#e5e5e5]">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  </section>
);
